import cx from 'classnames';
import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';

import { SNIPPET_GROUPING } from '../../../../../shared/featureFlags/flagNames';
import {
  defaultSnippetsSort,
  type Snippet,
  type SnippetPackage,
  type SnippetsQueryParams,
  type SnippetsSort,
} from '../../../../../shared/snippets';
import { useQuery } from '../../../../contexts/RouterContext';
import { useIsFeatureEnabled } from '../../../../helpers/features';
import { Checkbox, SortingDropdown } from '../../../generic';
import FallbackImage from '../../../generic/FallbackImage';
import List from '../../../generic/List';
import { TreeViewLayout } from '../../shared/TreeViewLayout';
import useSnippetGrouping from '../hooks/useSnippetGrouping';
import useSnippetsOrSnippetPackagesByUrl from '../hooks/useSnippetsOrSnippetPackagesByUrl';
import type { SortingDirection } from '../../../generic/SortingDropdown';
import type { SnippetSelectionState } from '../hooks/useSnippetSelectionState';

import BulkSelectionBanner from './BulkSelectionBanner';
import LoaderRow from './LoaderRow';
import SnippetPackageRow from './SnippetPackageRow';
import SnippetRow from './SnippetRow';

import styles from './index.module.less';

type Props = {
  userRevisionLocator: string;
  selectionState: SnippetSelectionState,
};

/**
 * Component which renders the table view of the snippets.
 */
const SnippetsTable: VoidFunctionComponent<Props> = ({
  userRevisionLocator,
  selectionState,
}) => {
  const [queryParams, setQuery] = useQuery<SnippetsQueryParams>();
  const { data, isLoading } = useSnippetsOrSnippetPackagesByUrl();

  const isSnippetGroupingEnabled = useIsFeatureEnabled(SNIPPET_GROUPING);
  const { snippetGrouping } = useSnippetGrouping();
  const isGroupingByPackage = isSnippetGroupingEnabled && snippetGrouping === 'package';

  // Check the URL params for a sorting value, otherwise use the default.
  const currentSort = queryParams?.sort ? queryParams.sort : defaultSnippetsSort;
  const setCurrentSort = (newSort?: SnippetsSort) => {
    setQuery({
      ...queryParams,
      sort: newSort,
      page: undefined,
    });
  };

  const getPackageSortingDirection = () => {
    if (currentSort === 'package_asc') {
      return 'asc';
    }
    if (currentSort === 'package_desc') {
      return 'desc';
    }
    return undefined;
  };

  const setPackageSortingDirection = (direction?: SortingDirection) => {
    if (direction === 'asc') {
      setCurrentSort('package_asc');
    } else if (direction === 'desc') {
      setCurrentSort('package_desc');
    } else {
      setCurrentSort(undefined);
    }
  };

  const getMatchCountSortingDirection = () => {
    if (currentSort === 'matchCount_asc') {
      return 'asc';
    }
    if (currentSort === 'matchCount_desc') {
      return 'desc';
    }
    return undefined;
  };

  const setMatchCountSortingDirection = (direction?: SortingDirection) => {
    if (direction === 'asc') {
      setCurrentSort('matchCount_asc');
    } else if (direction === 'desc') {
      setCurrentSort('matchCount_desc');
    } else {
      setCurrentSort(undefined);
    }
  };

  // Determine if the banner should be shown
  const hasResults = !!data?.results?.length;
  const shouldShowBanner = hasResults && selectionState.isPageFullySelected();

  const renderTableRows = () => {
    if (isLoading) {
      return _.times(queryParams.pageSize || 10, (i) => <LoaderRow key={i} />);
    }

    if (!data?.results?.length) {
      return <FallbackImage src="no-results" alt="No snippets found">No snippets found.</FallbackImage>;
    }

    if (isGroupingByPackage) {
      return data?.results?.map((snippetOrPackage, index) => (
        <SnippetPackageRow
          key={`package-${snippetOrPackage.id}`}
          // This type assertion is safe because we know we are grouping by package here.
          snippetPackage={snippetOrPackage as SnippetPackage}
          selectionState={selectionState}
          shouldShowBorderTop={index === 0 && shouldShowBanner}
          // We hardcode 10 (instead of using pageSize) because the table itself never gets
          // shorter than 10 rows high due to how the shared layout (with the file tree) is structured.
          shouldShowBorderBottom={index === data.results.length - 1 && data.results.length < 10}
        />
      ));
    }

    return data?.results?.map((snippetOrPackage, index) => (
      <SnippetRow
        key={`snippet-${snippetOrPackage.id}`}
        // This type assertion is safe because we know we're not grouping by package here.
        snippet={snippetOrPackage as Snippet}
        selectionState={selectionState}
        shouldShowBorderTop={index === 0 && shouldShowBanner}
        // We hardcode 10 (instead of using pageSize) because the table itself never gets
        // shorter than 10 rows high due to how the shared layout (with the file tree) is structured.
        shouldShowBorderBottom={index === data.results.length - 1 && data.results.length < 10}
        onSelectSnippet={(snippet) => setQuery({ ...queryParams, selectedSnippetId: snippet.id })}
      />
    ));
  };

  const tableHeader = (
    <div className={cx(styles.tableHeaderRow, { [styles.tableHeaderRowPackages]: isGroupingByPackage })}>
      {isGroupingByPackage && (
        // Empty span to align with expand/collapse checkbox in package rows
        <span />
      )}
      <List.HeaderText>
        <Checkbox
          isChecked={selectionState.isHeaderChecked()}
          isIndeterminate={selectionState.isHeaderIndeterminate()}
          onChange={() => selectionState.toggleHeader()}
          isRippleDisabled
        />
      </List.HeaderText>
      <SortingDropdown
        direction={getPackageSortingDirection()}
        setDirection={setPackageSortingDirection}
      >
        <List.HeaderText>Package</List.HeaderText>
      </SortingDropdown>
      <List.HeaderText>Version</List.HeaderText>
      <List.HeaderText>Issues</List.HeaderText>
      <SortingDropdown
        direction={getMatchCountSortingDirection()}
        setDirection={setMatchCountSortingDirection}
      >
        <List.HeaderText>Matches</List.HeaderText>
      </SortingDropdown>
      <List.HeaderText>Labels</List.HeaderText>
    </div>
  );

  return (
    <TreeViewLayout.Table header={tableHeader}>
      <div className={styles.table}>
        {shouldShowBanner && !!data && (
          <BulkSelectionBanner
            grouping={snippetGrouping}
            selectionState={selectionState}
            pageSize={data.results.length}
            totalItemsCount={data.totalCount}
            userRevisionLocator={userRevisionLocator}
          />
        )}
        <List.Body>
          {renderTableRows()}
        </List.Body>
      </div>
    </TreeViewLayout.Table>
  );
};

export default SnippetsTable;
